import React, { useEffect, useState, useCallback } from "react";
import { Tag, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";

// Member-facing transparency + consent panel (spec §2 edit, §6 privacy/opt-out).
export const AdInterests = () => {
  const [taxonomy, setTaxonomy] = useState([]);
  const [data, setData] = useState({ opt_out: false, explicit: [], feedback: [] });
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/ads/interests").then((r) => {
      setData(r.data);
      setSelected(new Set((r.data.explicit || []).map((x) => x.tag)));
    }).catch(() => {});
    api.get("/ads/taxonomy").then((r) => setTaxonomy(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (tag) => {
    const next = new Set(selected);
    next.has(tag) ? next.delete(tag) : next.add(tag);
    if (next.size <= 10) setSelected(next);
  };

  const save = () => {
    setSaving(true);
    api.put("/ads/interests", { tags: [...selected] })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setSaving(false));
  };

  const setOptOut = (v) => {
    api.post("/ads/optout", { opt_out: v }).then(() => load()).catch(() => {});
  };

  return (
    <div className="bg-race-surface border border-race-line rounded-lg p-5" data-testid="ad-interests">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2"><Tag size={16} className="text-race-red" />
          <h3 className="text-base font-semibold">Ad Interests</h3></div>
        <button onClick={() => setOptOut(!data.opt_out)} data-testid="ad-optout-toggle"
          className={`flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${data.opt_out ? "border-race-red text-race-red" : "border-race-line text-race-sub hover:text-race-txt"}`}>
          {data.opt_out ? <EyeOff size={13} /> : <Eye size={13} />}
          {data.opt_out ? "Personalization OFF" : "Turn off ad personalization"}
        </button>
      </div>
      <p className="text-xs text-race-sub mb-4">Pick a few things you like so sponsors are relevant. Editable any time. We never use your ID or payment info for ads.</p>

      {data.opt_out ? (
        <div className="text-sm text-race-sub" data-testid="ad-optout-state">
          Ad personalization is off and your interest data has been cleared. You'll only see general sponsors.
          <button onClick={() => setOptOut(false)} className="ml-2 text-race-red underline">Turn back on</button>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {taxonomy.map((cat) => (
              <div key={cat.category}>
                <div className="overline text-race-sub mb-1.5">{cat.category}</div>
                <div className="flex flex-wrap gap-2">
                  {cat.tags.map((tag) => {
                    const on = selected.has(tag);
                    return (
                      <button key={tag} onClick={() => toggle(tag)} data-testid={`ad-tag-${tag}`}
                        className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${on ? "bg-race-red text-race-bg border-race-red" : "border-race-line text-race-sub hover:border-race-red"}`}>
                        {tag}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-race-sub">{selected.size}/10 selected</span>
            <button onClick={save} disabled={saving} data-testid="ad-interests-save"
              className="bg-race-red text-race-bg text-sm font-semibold rounded-sm px-4 py-2 disabled:opacity-50">
              {saving ? "Saving…" : "Save interests"}
            </button>
          </div>
          {data.feedback?.length > 0 && (
            <div className="mt-4 pt-3 border-t border-race-line">
              <div className="overline text-race-sub mb-1">From your ad feedback</div>
              <div className="flex flex-wrap gap-2">
                {data.feedback.map((f) => (
                  <span key={f.tag} data-testid={`ad-feedback-${f.tag}`}
                    className={`text-[11px] rounded-full px-2 py-1 border ${f.weight > 0 ? "text-race-volt border-race-volt/40" : "text-race-sub border-race-line line-through"}`}>
                    {f.tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdInterests;
